import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Button, Dialog } from '@floorfile/ui'
import { PenLine } from 'lucide-react'
import { useCopy } from '@floorfile/i18n'
import type { AttachmentTarget, UploadedAttachment } from '../api'
import { attachmentsCopy } from '../copy'
import { LazyPhotoAnnotator } from './LazyPhotoAnnotator'

interface Pending {
  /** The bytes just captured — the preview and the annotator read these, so it works offline. */
  file: Blob
  original: UploadedAttachment
  target: AttachmentTarget
  /** Called ONCE, with the annotated copy if one was saved, else the original. */
  finish: (attachment: UploadedAttachment) => void
}

/**
 * F7 — markup at capture. A photo that was just taken is shown with **Mark up**
 * and **Done** before it reaches its host. Mark up opens the annotator on the
 * photo in hand; saving hands the host the ANNOTATED COPY (the original stays
 * as its `derivedFromAttachmentId`, and the server's claim carries it along —
 * see `carriedOriginals`). Done, or closing, hands over the original.
 *
 * One hook for every capture point, so "after any photo is taken" is one
 * behaviour rather than a dozen slightly different ones.
 */
export function useCaptureMarkup(): { offer: (pending: Pending) => void; dialog: ReactNode } {
  // Warm the (code-split) editor while the screen is online: a photo is most
  // often taken exactly where the signal is not, and a lazy chunk that has not
  // been fetched cannot be fetched there. Idempotent — the module loader caches
  // the promise — and off the critical path.
  useEffect(() => {
    void import('./PhotoAnnotator').catch(() => undefined)
  }, [])
  const [pending, setPending] = useState<Pending | undefined>(undefined)
  const [annotating, setAnnotating] = useState(false)
  const offer = useCallback((next: Pending) => {
    setAnnotating(false)
    setPending(next)
  }, [])
  const done = useCallback(
    (attachment: UploadedAttachment) => {
      pending?.finish(attachment)
      setPending(undefined)
      setAnnotating(false)
    },
    [pending],
  )

  const dialog = !pending ? null : annotating ? (
    <LazyPhotoAnnotator
      source={pending.original}
      sourceBlob={pending.file}
      target={pending.target}
      // Cancelling the annotator returns to the preview, not to nothing.
      onClose={() => setAnnotating(false)}
      onSaved={(derivative) => done(derivative)}
    />
  ) : (
    <CapturePreview file={pending.file} fileName={pending.original.fileName} onMarkup={() => setAnnotating(true)} onDone={() => done(pending.original)} />
  )
  return { offer, dialog }
}

/** True when a picked file is a photo worth offering markup on. */
export function isMarkupCandidate(file: Blob): boolean {
  return file.type.startsWith('image/') && file.type !== 'image/svg+xml' && file.type !== 'image/gif'
}

function CapturePreview({ file, fileName, onMarkup, onDone }: { file: Blob; fileName: string; onMarkup: () => void; onDone: () => void }) {
  const c = useCopy(attachmentsCopy).markup
  const [url, setUrl] = useState<string | undefined>(undefined)
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])
  return (
    <Dialog open onClose={onDone} title={c.title} className="max-w-lg">
      <div className="space-y-3">
        {url && <img src={url} alt={c.alt({ fileName })} className="mx-auto max-h-[50vh] w-auto max-w-full rounded-md border object-contain" />}
        <p className="text-sm text-muted-foreground">{c.intro}</p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onMarkup} className="gap-1.5">
            <PenLine className="h-4 w-4" aria-hidden />
            {c.markUp}
          </Button>
          <Button onClick={onDone}>{c.done}</Button>
        </div>
      </div>
    </Dialog>
  )
}
